import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StepNav } from '@/components/verification/step-nav';
import { FlowCancelConfirmDialog } from '@/components/verification/flow-cancel-dialog';
import { ROUTES } from '@/lib/constants';
import { useCaseBuilderStore } from '@/features/employer-verification-requests/store';

const STEPS = [
  { to: ROUTES.employerVerificationRequestsNewPricing, label: 'Pricing' },
  { to: ROUTES.employerVerificationRequestsNewOtp, label: 'Verify' },
  { to: ROUTES.employerVerificationRequestsNewBuilder, label: 'Build case' },
  { to: ROUTES.employerVerificationRequestsNewConsent, label: 'Consent' },
  { to: ROUTES.employerVerificationRequestsNewConfirm, label: 'Confirm' },
];

// Warn before leaving while there's unsaved case-builder work.
const GUARDED = new Set<string>([
  ROUTES.employerVerificationRequestsNewBuilder,
  ROUTES.employerVerificationRequestsNewConsent,
]);

export function CaseBuilderLayout() {
  const reset = useCaseBuilderStore((s) => s.reset);
  const receipt = useCaseBuilderStore((s) => s.receipt);
  const navigate = useNavigate();
  const location = useLocation();
  const [cancelOpen, setCancelOpen] = useState(false);

  const guarded = GUARDED.has(location.pathname) && !receipt;

  useEffect(() => {
    if (!guarded) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [guarded]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.employerVerificationRequests)}>
          <ArrowLeft className="h-4 w-4" /> Requests
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)}>
          Cancel <X className="h-4 w-4" />
        </Button>
      </div>

      <StepNav steps={STEPS} />

      <Card className="p-6 sm:p-8">
        <Outlet />
      </Card>

      <FlowCancelConfirmDialog
        open={cancelOpen}
        onStay={() => setCancelOpen(false)}
        title="Leave the case builder?"
        description="Your draft will be discarded and you'll need to re-verify to start again."
        confirmText="Discard and exit"
        onConfirmCancel={() => {
          setCancelOpen(false);
          reset();
          navigate(ROUTES.employerVerificationRequests, { replace: true });
        }}
      />
    </div>
  );
}
